import React, { useState } from 'react';
import {
  Card,
  CardHeader,
  CardContent,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Box,
  Typography,
  Chip,
  IconButton,
  Collapse,
  Divider,
  Grid,
  LinearProgress,
} from '@mui/material';
import {
  Cloud as CloudIcon,
  CloudDone as CloudDoneIcon,
  CloudOff as CloudOffIcon,
  Warning as WarningIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  OpenInNew as OpenInNewIcon,
  Refresh as RefreshIcon,
  Speed as SpeedIcon,
  Memory as MemoryIcon,
  Storage as StorageIcon,
} from '@mui/icons-material';
import { Environment, ApplicationInfo } from '../hooks/useApplicationObservatory';

interface EnvironmentsPanelProps {
  environments: Environment[];
  applications: ApplicationInfo[];
  compact?: boolean;
  onEnvironmentClick?: (env: Environment) => void;
  onRefreshHealth?: (envId: string) => void;
}

export const EnvironmentsPanel: React.FC<EnvironmentsPanelProps> = ({
  environments,
  applications,
  compact = false,
  onEnvironmentClick,
  onRefreshHealth,
}) => {
  const [expandedEnv, setExpandedEnv] = useState<string | null>(null);

  const getStatusIcon = (status: Environment['status']) => {
    switch (status) {
      case 'healthy':
        return <CloudDoneIcon color="success" />;
      case 'degraded':
        return <WarningIcon color="warning" />;
      case 'unhealthy':
        return <CloudOffIcon color="error" />;
      default:
        return <CloudIcon color="disabled" />;
    }
  };

  const getStatusColor = (status: Environment['status']): 'success' | 'warning' | 'error' | 'default' => {
    switch (status) {
      case 'healthy':
        return 'success';
      case 'degraded':
        return 'warning';
      case 'unhealthy':
        return 'error';
      default:
        return 'default';
    }
  };

  const getTypeColor = (type: Environment['type']): 'error' | 'warning' | 'info' => {
    switch (type) {
      case 'production':
        return 'error';
      case 'staging':
        return 'warning';
      case 'development':
        return 'info';
      default:
        return 'info';
    }
  };

  const formatDate = (date?: Date) => {
    if (!date) return 'Never';
    const d = new Date(date);
    const now = new Date();
    const diff = now.getTime() - d.getTime();
    
    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    return d.toLocaleTimeString();
  };

  // Mock health metrics for demonstration
  const getMockHealthMetrics = (envId: string) => {
    const seed = envId.charCodeAt(4) || 1;
    return {
      cpu: Math.min(95, 20 + (seed * 15) % 60),
      memory: Math.min(90, 30 + (seed * 12) % 50),
      responseTime: 100 + (seed * 50) % 300,
      requestsPerMin: 50 + (seed * 30) % 200,
      errorRate: ((seed * 0.3) % 2).toFixed(1),
    };
  };

  return (
    <Card>
      <CardHeader
        title={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CloudIcon />
            <Typography variant="h6">Environments</Typography>
            <Chip label={environments.length} size="small" color="primary" />
          </Box>
        }
        subheader="Deployment targets and their health status"
      />
      <CardContent>
        {/* Summary */}
        <Box sx={{ mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Chip
            icon={<CloudDoneIcon />}
            label={`${environments.filter(e => e.status === 'healthy').length} Healthy`}
            size="small"
            color="success"
            variant="outlined"
          />
          {environments.filter(e => e.status === 'degraded').length > 0 && (
            <Chip
              icon={<WarningIcon />}
              label={`${environments.filter(e => e.status === 'degraded').length} Degraded`}
              size="small"
              color="warning"
              variant="outlined"
            />
          )}
          {environments.filter(e => e.status === 'unhealthy').length > 0 && (
            <Chip
              icon={<CloudOffIcon />}
              label={`${environments.filter(e => e.status === 'unhealthy').length} Unhealthy`}
              size="small"
              color="error"
              variant="outlined"
            />
          )}
        </Box>

        {/* Environments List */}
        {environments.length === 0 ? (
          <Typography color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
            No environments configured
          </Typography>
        ) : (
          <List sx={{ maxHeight: compact ? 300 : 600, overflow: 'auto' }}>
            {environments.map((env) => {
              const deployedApps = applications.filter(app => 
                env.applications.includes(app.id)
              );
              const healthMetrics = getMockHealthMetrics(env.id);

              return (
                <React.Fragment key={env.id}>
                  <ListItem
                    sx={{
                      mb: 1,
                      bgcolor: 'background.default',
                      borderRadius: 1,
                      border: 1,
                      borderColor: env.status === 'unhealthy' ? 'error.light' :
                                  env.status === 'degraded' ? 'warning.light' : 'divider',
                      cursor: 'pointer',
                      '&:hover': { bgcolor: 'action.hover' },
                    }}
                    onClick={() => {
                      if (compact) {
                        onEnvironmentClick?.(env);
                      } else {
                        setExpandedEnv(expandedEnv === env.id ? null : env.id);
                      }
                    }}
                  >
                    <ListItemIcon>
                      {getStatusIcon(env.status)}
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            {env.name}
                          </Typography>
                          <Chip
                            label={env.type}
                            size="small"
                            color={getTypeColor(env.type)}
                          />
                          <Chip
                            label={env.status}
                            size="small"
                            color={getStatusColor(env.status)}
                            variant="outlined"
                          />
                          <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
                            {deployedApps.length} apps deployed
                          </Typography>
                        </Box>
                      }
                      secondary={
                        <Box sx={{ mt: 0.5 }}>
                          <Typography
                            variant="caption"
                            sx={{ fontFamily: 'monospace' }}
                          >
                            {env.url}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            Last health check: {formatDate(env.lastHealthCheck)}
                          </Typography>
                        </Box>
                      }
                    />
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        onRefreshHealth?.(env.id);
                      }}
                    >
                      <RefreshIcon />
                    </IconButton>
                    {!compact && (
                      <IconButton size="small">
                        {expandedEnv === env.id ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      </IconButton>
                    )}
                  </ListItem>

                  {!compact && (
                    <Collapse in={expandedEnv === env.id} timeout="auto" unmountOnExit>
                      <Box sx={{ pl: 7, pr: 2, pb: 2 }}>
                        <Divider sx={{ my: 1 }} />
                        
                        {/* Health Metrics */}
                        <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                          Health Metrics
                        </Typography>
                        <Grid container spacing={2} sx={{ mb: 2 }}>
                          <Grid item xs={6} md={3}>
                            <Box sx={{ textAlign: 'center', p: 1, bgcolor: 'background.paper', borderRadius: 1 }}>
                              <SpeedIcon sx={{ color: 'primary.main' }} />
                              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                {healthMetrics.cpu}%
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                CPU Usage
                              </Typography>
                              <LinearProgress
                                variant="determinate"
                                value={healthMetrics.cpu}
                                color={healthMetrics.cpu > 80 ? 'error' : healthMetrics.cpu > 60 ? 'warning' : 'success'}
                                sx={{ mt: 1, height: 4, borderRadius: 2 }}
                              />
                            </Box>
                          </Grid>
                          <Grid item xs={6} md={3}>
                            <Box sx={{ textAlign: 'center', p: 1, bgcolor: 'background.paper', borderRadius: 1 }}>
                              <MemoryIcon sx={{ color: 'secondary.main' }} />
                              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                {healthMetrics.memory}%
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                Memory Usage
                              </Typography>
                              <LinearProgress
                                variant="determinate"
                                value={healthMetrics.memory}
                                color={healthMetrics.memory > 80 ? 'error' : healthMetrics.memory > 60 ? 'warning' : 'success'}
                                sx={{ mt: 1, height: 4, borderRadius: 2 }}
                              />
                            </Box>
                          </Grid>
                          <Grid item xs={6} md={3}>
                            <Box sx={{ textAlign: 'center', p: 1, bgcolor: 'background.paper', borderRadius: 1 }}>
                              <StorageIcon sx={{ color: 'info.main' }} />
                              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                {healthMetrics.responseTime}ms
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                Avg Response
                              </Typography>
                            </Box>
                          </Grid>
                          <Grid item xs={6} md={3}>
                            <Box sx={{ textAlign: 'center', p: 1, bgcolor: 'background.paper', borderRadius: 1 }}>
                              <SpeedIcon sx={{ color: 'success.main' }} />
                              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                {healthMetrics.requestsPerMin}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                Requests/min
                              </Typography>
                            </Box>
                          </Grid>
                        </Grid>

                        {/* Deployed Applications */}
                        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                          Deployed Applications ({deployedApps.length})
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                          {deployedApps.map(app => (
                            <Chip
                              key={app.id}
                              label={app.name}
                              size="small"
                              color={app.status === 'published' ? 'success' : 'warning'}
                              variant="outlined"
                            />
                          ))}
                          {deployedApps.length === 0 && (
                            <Typography variant="caption" color="text.secondary">
                              No applications deployed
                            </Typography>
                          )}
                        </Box>

                        {/* Actions */}
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Chip
                            label="View Logs"
                            size="small"
                            clickable
                            onClick={() => console.log('View logs for', env.id)}
                          />
                          <Chip
                            label="View Traces"
                            size="small"
                            clickable
                            onClick={() => console.log('View traces for', env.id)}
                          />
                          <Chip
                            label="Monitor"
                            size="small"
                            icon={<OpenInNewIcon />}
                            clickable
                            onClick={() => console.log('Open monitor for', env.id)}
                          />
                        </Box>
                      </Box>
                    </Collapse>
                  )}
                </React.Fragment>
              );
            })}
          </List>
        )}
      </CardContent>
    </Card>
  );
};

export default EnvironmentsPanel;

